/**
 * SwiperJS Infinite Scroll Coordinator for Sunice Slideshows
 * Coordinates initialization of different swiper types using separate modules.
 * Optimized for mobile: deferred to idle, lazy modules, staggered init to reduce main-thread work.
 */

class SuniceSwiperInfinite {
  constructor() {
    this.swiperModules = new Map();
    this.init();
  }

  init() {
    const initializeWhenReady = () => {
      const modulesReady = typeof Swiper !== 'undefined' && 
                          (typeof window.SuniceProductGridSwiper !== 'undefined' || 
                           typeof window.SuniceColorSwiper !== 'undefined');
      if (modulesReady || document.readyState === 'complete') {
        setTimeout(() => this.setupSwiperSlideshows(), 100);
      } else {
        setTimeout(initializeWhenReady, 100);
      }
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeWhenReady);
    } else {
      initializeWhenReady();
    }
  }

  /** Lazy: create module only when first needed (reduces upfront parse/execution). */
  getModule(key) {
    if (this.swiperModules.has(key)) return this.swiperModules.get(key);
    let module = null;
    switch (key) {
      case 'productGrid':
        if (window.SuniceProductGridSwiper) {
          module = new window.SuniceProductGridSwiper();
          this.swiperModules.set(key, module);
        }
        break;
      case 'targetedProducts':
        if (window.SuniceTargetedProductsSwiper) {
          module = new window.SuniceTargetedProductsSwiper();
          this.swiperModules.set(key, module);
        }
        break;
      case 'recommendedProducts':
        if (window.SuniceRecommendedProductsSwiper) {
          module = new window.SuniceRecommendedProductsSwiper();
          this.swiperModules.set(key, module);
        }
        break;
      case 'colorSwiper':
        if (window.SuniceColorSwiperInstance) {
          module = window.SuniceColorSwiperInstance;
          this.swiperModules.set(key, module);
        } else if (window.SuniceColorSwiper) {
          module = new window.SuniceColorSwiper();
          this.swiperModules.set(key, module);
        }
        break;
    }
    return module;
  }

  setupSwiperSlideshows() {
    this.initializeMainSwipers();
    this.initializeColorSwatchSwipers();
  }

  /** Run one or more inits in next idle (or setTimeout) to avoid long tasks. */
  scheduleIdle(fn) {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(fn, { timeout: 600 });
    } else {
      setTimeout(fn, 0);
    }
  }

  /** Initialize one product grid container; then schedule next if any (staggered for TBT). */
  initProductGridNext(containers, index) {
    if (index >= containers.length) {
      this.scheduleIdle(() => this.initTargetedProducts());
      return;
    }
    const container = containers[index];
    const productGridModule = this.getModule('productGrid');
    if (productGridModule) {
      if (!container.id) container.id = `product-grid-swiper-${Date.now()}-${index}`;
      const slideCount = container.querySelectorAll('.swiper-slide').length;
      if (slideCount > 0) productGridModule.initializeProductGridSwiper(container);
    }
    this.scheduleIdle(() => this.initProductGridNext(containers, index + 1));
  }

  initTargetedProducts() {
    const list = document.querySelectorAll('.sunice-targeted-products-swiper');
    const module = list.length ? this.getModule('targetedProducts') : null;
    if (module) list.forEach(el => module.initializeTargetedProductsSwiper(el));
    this.scheduleIdle(() => this.initRecommendedProducts());
  }

  initRecommendedProducts() {
    const list = document.querySelectorAll('.sunice-recommended-products-swiper');
    const module = list.length ? this.getModule('recommendedProducts') : null;
    if (module) list.forEach(el => module.initializeRecommendedSwiper(el));
  }

  initializeMainSwipers(retryCount = 0) {
    const MAX_RETRIES = 10;
    let containers = document.querySelectorAll('[id^="product-grid-swiper-"]');
    if (containers.length === 0) containers = document.querySelectorAll('.sunice-product-grid-swiper');
    if (containers.length === 0) {
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => this.initializeMainSwipers(retryCount + 1), 200);
        return;
      }
      this.scheduleIdle(() => this.initTargetedProducts());
      return;
    }
    this.initProductGridNext(Array.from(containers), 0);
  }

  initializeColorSwatchSwipers() {
    this.getModule('colorSwiper');
  }

  destroyAllSwipers() {
    // Destroy all swipers in each module
    this.swiperModules.forEach((module, moduleName) => {
      if (module && typeof module.destroyProductGridSwipers === 'function') {
        module.destroyProductGridSwipers();
      }
      if (module && typeof module.destroyTargetedProductsSwipers === 'function') {
        module.destroyTargetedProductsSwipers();
      }
      if (module && typeof module.destroyRecommendedProductsSwipers === 'function') {
        module.destroyRecommendedProductsSwipers();
      }
      if (module && typeof module.destroyColorSwipers === 'function') {
        module.destroyColorSwipers();
      }
    });
    // Don't clear modules - just destroy instances
    // this.swiperModules.clear();
    
    // Reset all initialization flags
    document.querySelectorAll('.sunice-product-grid-swiper, .sunice-targeted-products-swiper, .sunice-recommended-products-swiper, [id^="product-grid-swiper-"]').forEach(container => {
      delete container.dataset.initStarted;
      container.classList.remove('swiper-initialized');
    });
  }
}

// Fast check: any Sunice swiper container on the page (avoids work when none)
function hasAnySwiperContainer() {
  return document.querySelector('.sunice-product-grid-swiper, .sunice-targeted-products-swiper, .sunice-recommended-products-swiper, [id^="product-grid-swiper-"]') !== null;
}

function runSuniceSwiperInfinite() {
  if (typeof window.SuniceProductGridSwiper === 'undefined') {
    setTimeout(runSuniceSwiperInfinite, 50);
    return;
  }
  if (!hasAnySwiperContainer()) return;
  const suniceSwiperInfinite = new SuniceSwiperInfinite();
  window.suniceSwiperInfinite = suniceSwiperInfinite;
}

function scheduleSuniceSwiperInfinite() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleSuniceSwiperInfinite);
    return;
  }
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(runSuniceSwiperInfinite, { timeout: 2000 });
  } else {
    setTimeout(runSuniceSwiperInfinite, 300);
  }
}
scheduleSuniceSwiperInfinite();

// Helper function to destroy swipers in a specific section
function destroySwipersInSection(section) {
  if (!section || !window.suniceSwiperInfinite) return;
  
  // Find all swiper containers in the section
  const swiperContainers = section.querySelectorAll(
    '.sunice-product-grid-swiper, .sunice-targeted-products-swiper, .sunice-recommended-products-swiper, [id^="product-grid-swiper-"]'
  );
  
  swiperContainers.forEach(container => {
    // Reset initialization flags
    delete container.dataset.initStarted;
    container.classList.remove('swiper-initialized');
    
    // Destroy swiper instances from all modules
    const modules = window.suniceSwiperInfinite.swiperModules;
    
    // Product Grid
    const productGridModule = modules.get('productGrid');
    if (productGridModule && container.id && productGridModule.swiperInstances) {
      const existingSwiper = productGridModule.swiperInstances.get(container.id);
      if (existingSwiper && existingSwiper.destroy) {
        existingSwiper.destroy(true, true);
        productGridModule.swiperInstances.delete(container.id);
      }
    }
    
    // Targeted Products
    const targetedProductsModule = modules.get('targetedProducts');
    if (targetedProductsModule && container.id && targetedProductsModule.swiperInstances) {
      const existingSwiper = targetedProductsModule.swiperInstances.get(container.id);
      if (existingSwiper && existingSwiper.destroy) {
        existingSwiper.destroy(true, true);
        targetedProductsModule.swiperInstances.delete(container.id);
      }
    }
    
    // Recommended Products
    const recommendedProductsModule = modules.get('recommendedProducts');
    if (recommendedProductsModule && container.id && recommendedProductsModule.swiperInstances) {
      const existingSwiper = recommendedProductsModule.swiperInstances.get(container.id);
      if (existingSwiper && existingSwiper.destroy) {
        existingSwiper.destroy(true, true);
        recommendedProductsModule.swiperInstances.delete(container.id);
      }
    }
  });
}

// OPTIMIZED: Re-initialize when theme editor updates sections
document.addEventListener('shopify:section:load', (event) => {
  if (!window.suniceSwiperInfinite && hasAnySwiperContainer()) runSuniceSwiperInfinite();
  if (!window.suniceSwiperInfinite) return;
  
  // Get the section element from event
  let section = event.target;
  if (!section || !(section instanceof HTMLElement)) {
    // Try to get section from event.detail.sectionId
    const sectionId = event.detail?.sectionId;
    if (sectionId) {
      section = document.querySelector(`[data-section-id="${sectionId}"]`) || 
                document.querySelector(`section[data-section-id="${sectionId}"]`) ||
                document.querySelector(`#shopify-section-${sectionId}`);
    }
  }
  
  // Use double requestAnimationFrame to ensure DOM is fully updated
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (section && section instanceof HTMLElement) {
        // Destroy swipers only in the updated section
        destroySwipersInSection(section);
        
        // Re-initialize swipers in this section only
        const productGridModule = window.suniceSwiperInfinite.swiperModules.get('productGrid');
        const targetedProductsModule = window.suniceSwiperInfinite.swiperModules.get('targetedProducts');
        const recommendedProductsModule = window.suniceSwiperInfinite.swiperModules.get('recommendedProducts');
        
        // Re-initialize product grid swipers
        const productGridSwipers = section.querySelectorAll('.sunice-product-grid-swiper, [id^="product-grid-swiper-"]');
        if (productGridModule && productGridSwipers.length > 0) {
          productGridSwipers.forEach(container => {
            productGridModule.initializeProductGridSwiper(container);
          });
        }
        
        // Re-initialize targeted products swipers
        const targetedProductsSwipers = section.querySelectorAll('.sunice-targeted-products-swiper');
        if (targetedProductsModule && targetedProductsSwipers.length > 0) {
          targetedProductsSwipers.forEach(container => {
            targetedProductsModule.initializeTargetedProductsSwiper(container);
          });
        }
        
        // Re-initialize recommended products swipers
        const recommendedProductsSwipers = section.querySelectorAll('.sunice-recommended-products-swiper');
        if (recommendedProductsModule && recommendedProductsSwipers.length > 0) {
          recommendedProductsSwipers.forEach(container => {
            recommendedProductsModule.initializeRecommendedSwiper(container);
          });
        }
      } else {
        window.suniceSwiperInfinite.destroyAllSwipers();
        window.suniceSwiperInfinite.initializeMainSwipers();
        window.suniceSwiperInfinite.initializeColorSwatchSwipers();
      }
    });
  });
});

var _swiperReorderTimeout = null;
document.addEventListener('shopify:section:reorder', () => {
  if (!window.suniceSwiperInfinite) return;
  if (_swiperReorderTimeout) clearTimeout(_swiperReorderTimeout);
  _swiperReorderTimeout = setTimeout(() => {
    _swiperReorderTimeout = null;
    window.suniceSwiperInfinite.destroyAllSwipers();
    window.suniceSwiperInfinite.initializeMainSwipers();
    window.suniceSwiperInfinite.initializeColorSwatchSwipers();
  }, 300);
});

// section:select and section:deselect don't change DOM — no re-init needed

// OPTIMIZED: Init all swipers in lazy-loaded section immediately so section can reveal without delay
document.addEventListener('section:lazy-load', (event) => {
  const section = event.target;
  if (!section) return;

  if (!window.suniceSwiperInfinite && typeof runSuniceSwiperInfinite === 'function') {
    runSuniceSwiperInfinite();
  }
  if (!window.suniceSwiperInfinite) return;

  const inf = window.suniceSwiperInfinite;

  // Product grid – init sync so sectionSwipersReady passes on next frame
  const productGridSwipers = section.querySelectorAll('.sunice-product-grid-swiper, [id^="product-grid-swiper-"]');
  if (productGridSwipers.length > 0) {
    const productGridModule = inf.getModule('productGrid');
    if (productGridModule) {
      productGridSwipers.forEach(container => {
        if (container.dataset.initStarted) delete container.dataset.initStarted;
        container.classList.remove('swiper-initialized');
        productGridModule.initializeProductGridSwiper(container);
      });
    }
  }

  // Targeted products
  const targetedSwipers = section.querySelectorAll('.sunice-targeted-products-swiper');
  if (targetedSwipers.length > 0) {
    const targetedModule = inf.getModule('targetedProducts');
    if (targetedModule) {
      targetedSwipers.forEach(container => {
        container.classList.remove('swiper-initialized');
        targetedModule.initializeTargetedProductsSwiper(container);
      });
    }
  }

  // Recommended products
  const recommendedSwipers = section.querySelectorAll('.sunice-recommended-products-swiper');
  if (recommendedSwipers.length > 0) {
    const recommendedModule = inf.getModule('recommendedProducts');
    if (recommendedModule) {
      recommendedSwipers.forEach(container => {
        container.classList.remove('swiper-initialized');
        recommendedModule.initializeRecommendedSwiper(container);
      });
    }
  }

  // Color swatch carousels
  const colorSwiperContainers = section.querySelectorAll('.color-swiper-container:not([data-initialized])');
  if (colorSwiperContainers.length > 0 && window.SuniceColorSwiperInstance) {
    colorSwiperContainers.forEach(container => {
      window.SuniceColorSwiperInstance.initializeColorSwiper(container);
    });
  }
});
